import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import CollectionHero from '../components/CollectionHero';
import ProductCard from '../components/ProductCard';
import ProductFilters from '../components/ProductFilters';
import { StoreEmpty, StoreError, StoreLoading } from '../components/StoreState';
import { useProducts } from '../hooks/useProducts';
import {
  fetchCollectionHeaders,
  resolveCollectionHeaderEntry,
} from '../lib/collectionHeaders';
import { getProductImageUrl } from '../utils/imageUrl';
import { parseFilterParams, productMatchesFilters } from '../utils/filters';
import {
  findLeagueBySlug,
  findTeamBySlug,
  getSportBySlug,
  groupProductsByTeam,
  productBelongsToLeague,
  productBelongsToSport,
  productBelongsToTeam,
  teamToSlug,
} from '../utils/sports';
import './Sports.css';

const SPORT_TONES = {
  'formula-series': '#1c1c1c',
  football: '#1a2420',
  basketball: '#241c18',
  baseball: '#1a1f24',
  volleyball: '#222018',
  cricket: '#18241c',
  rugby: '#241818',
};

function Sports() {
  const { sportSlug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => parseFilterParams(searchParams), [searchParams]);
  const { products, loading, error } = useProducts();
  const sport = getSportBySlug(sportSlug);
  const [collectionHeaders, setCollectionHeaders] = useState({});

  const leagueSlug = searchParams.get('league') || '';
  const teamSlug = searchParams.get('team') || '';

  const selectedLeague = findLeagueBySlug(sport, leagueSlug);
  const selectedTeam = findTeamBySlug(sport, teamSlug);

  useEffect(() => {
    let cancelled = false;
    fetchCollectionHeaders()
      .then(headers => {
        if (!cancelled) setCollectionHeaders(headers);
      })
      .catch(() => {
        if (!cancelled) setCollectionHeaders({});
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!sport) return;
    if (leagueSlug && !selectedLeague) {
      const next = new URLSearchParams(searchParams);
      next.delete('league');
      next.delete('team');
      setSearchParams(next, { replace: true });
    }
  }, [sport, leagueSlug, selectedLeague, searchParams, setSearchParams]);

  const sportProducts = useMemo(() => {
    if (!sport) return [];
    return products.filter(product => productBelongsToSport(product, sport));
  }, [products, sport]);

  const cover = useMemo(() => {
    if (!sport) return { imageUrl: null, crop: undefined };

    const leagueForTeam =
      selectedLeague ||
      (selectedTeam
        ? (sport.leagues || []).find(league =>
            (league.teams || []).some(
              team => teamToSlug(team) === teamToSlug(selectedTeam)
            )
          )
        : null);

    const uploaded = resolveCollectionHeaderEntry(collectionHeaders, {
      sportSlug: sport.slug,
      leagueSlug: leagueForTeam?.slug || selectedLeague?.slug || '',
      teamSlug: selectedTeam ? teamToSlug(selectedTeam) : '',
    });
    if (uploaded) {
      return { imageUrl: uploaded.image_url, crop: uploaded.header_crop };
    }

    const first = sportProducts.find(product => getProductImageUrl(product));
    return { imageUrl: first ? getProductImageUrl(first) : null };
  }, [sport, collectionHeaders, sportProducts, selectedLeague, selectedTeam]);

  const filteredProducts = useMemo(() => {
    let result = sportProducts;

    if (selectedLeague) {
      result = result.filter(product =>
        productBelongsToLeague(product, selectedLeague, sport)
      );
    }

    if (selectedTeam) {
      result = result.filter(product =>
        productBelongsToTeam(product, selectedTeam, sport)
      );
    }

    return result.filter(product => productMatchesFilters(product, filters));
  }, [sportProducts, selectedLeague, selectedTeam, filters, sport]);

  const grouped = useMemo(() => {
    if (selectedTeam) {
      return [{ team: selectedTeam, products: filteredProducts }];
    }
    return groupProductsByTeam(filteredProducts);
  }, [filteredProducts, selectedTeam]);

  if (!sport) {
    return (
      <main className="sports-page">
        <StoreEmpty
          eyebrow="Sports"
          title="Sport not found"
          lede="That discipline is not in the house yet."
          actionLabel="Back home"
          actionTo="/"
        />
      </main>
    );
  }

  const title = selectedTeam
    ? selectedTeam
    : selectedLeague
      ? selectedLeague.name
      : sport.name;

  return (
    <main className="sports-page">
      <CollectionHero
        title={title}
        imageUrl={cover.imageUrl}
        crop={cover.crop}
        tone={SPORT_TONES[sport.slug] || '#1a1a1a'}
      />

      <div className="sports-body">
        <div className="sports-layout">
          <ProductFilters />

          <div className="sports-results">
            {loading && <StoreLoading label="Loading kit…" skeletons={6} />}
            {error && <StoreError message={error} />}

            {!loading && !error && !filteredProducts.length && (
              <StoreEmpty
                eyebrow={sport.name}
                title="No pieces match"
                lede="Try clearing filters or browse another team."
                actionLabel="Clear filters"
                actionTo={`/sports/${sport.slug}`}
              />
            )}

            {grouped.map(group => (
              <section key={group.team} className="sports-group">
                {!selectedTeam && <h2 className="sports-group-title">{group.team}</h2>}
                <div className="product-card-grid">
                  {group.products.map(product => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </div>
              </section>
            ))}
          </div>
        </div>
      </div>
    </main>
  );
}

export default Sports;
